/**
 * useMonthlyExpenses Hook
 * Fetches and returns monthly expense data from the backend API.
 */

import { useQuery } from '@tanstack/react-query';
import { transactionApi } from '../api/transactionApi';

export const monthlyExpensesKeys = {
  all: ['monthlyExpenses'] as const,
  current: () => [...monthlyExpensesKeys.all, 'current'] as const,
  byPeriod: (startDate: string, endDate: string) =>
    [...monthlyExpensesKeys.all, startDate, endDate] as const,
};

/**
 * Get start and end dates for the current month
 */
function getCurrentMonthDates(): { startDate: string; endDate: string } {
  const now = new Date();
  const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
  const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);

  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  };
}

export interface MonthlyExpenseSummary {
  totalExpenses: number;
  totalIncome: number;
  netBalance: number;
  transactionCount: number;
}

export interface CategoryExpense {
  categoryId: string;
  categoryName: string;
  categoryColor: string;
  totalAmount: number;
  transactionCount: number;
  percentage: number;
}

/**
 * Hook for fetching current month expenses
 */
export function useMonthlyExpenses() {
  const { startDate, endDate } = getCurrentMonthDates();

  const query = useQuery({
    queryKey: monthlyExpensesKeys.current(),
    queryFn: () => transactionApi.getExpensesByCategory(startDate, endDate),
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: true,
  });

  const data = query.data?.success ? query.data.data : null;

  return {
    expenses: (data?.expenses || []) as CategoryExpense[],
    summary: (data?.summary || {
      totalExpenses: 0,
      totalIncome: 0,
      netBalance: 0,
      transactionCount: 0,
    }) as MonthlyExpenseSummary,
    period: data?.period || { startDate, endDate },
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook for fetching expenses for a specific month
 */
export function useExpensesByMonth(year: number, month: number) {
  const startDate = new Date(year, month, 1);
  const endDate = new Date(year, month + 1, 0, 23, 59, 59, 999);

  const query = useQuery({
    queryKey: monthlyExpensesKeys.byPeriod(startDate.toISOString(), endDate.toISOString()),
    queryFn: () =>
      transactionApi.getExpensesByCategory(
        startDate.toISOString(),
        endDate.toISOString()
      ),
    staleTime: 2 * 60 * 1000,
  });

  const data = query.data?.success ? query.data.data : null;

  return {
    expenses: (data?.expenses || []) as CategoryExpense[],
    summary: (data?.summary || {
      totalExpenses: 0,
      totalIncome: 0,
      netBalance: 0,
      transactionCount: 0,
    }) as MonthlyExpenseSummary,
    period: data?.period || {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    },
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
